import { ReviewFilterItem } from "./models/review-filter-item";
import { ReviewFilter } from "./models/review-filter";
import { FilteredPagedQueryModel } from "./models/filtered-paged-query-model";
import { NameValue } from "projects/sepidz-web-framework/src/lib/models/name-value";
import { TranslateService } from "@ngx-translate/core";

export const REVIEW_START_DATE_TIME_KEY = "StartDate";
export const REVIEW_END_DATE_TIME_KEY = "EndDate";
export const REVIEW_INCLUDE_TIME_KEY = "IncludeTime";
export const REVIEW_DATE_TIME_KEYES: string[] = [REVIEW_START_DATE_TIME_KEY, REVIEW_END_DATE_TIME_KEY, REVIEW_INCLUDE_TIME_KEY];

export interface Group<T> {
  key: string;
  members: T[];
  valueType: "array" | "boolean" | "joinedString" | "text" | "number";
}

export abstract class ReviewHelper {
  public static buildQuery(filter: ReviewFilter): FilteredPagedQueryModel {
    return <FilteredPagedQueryModel>{
      filters: ReviewHelper.generateNameValue(filter),
    };
  }

  public static generateNameValue(filter: ReviewFilter): NameValue[] {
    const dateFilters = filter.items.filter((c) => REVIEW_DATE_TIME_KEYES.indexOf(c.key) >= 0);

    const dateFiltersMap = dateFilters.map((d: any) => <NameValue>{ Name: d.key, Value: d.value });
    const arrayFilters = filter.items.filter((c) => REVIEW_DATE_TIME_KEYES.indexOf(c.key) === -1);
    const arrayFiltersMap = ReviewHelper.groupBy(arrayFilters, (c) => c.key).map((group: Group<any>) => {
      if (group.valueType === "boolean" && group.members.length > 1) {
        throw Error("Boolean ValueType Should Contain Just 1 Item");
      }
      let value;
      if (group.valueType === undefined || group.valueType === "array") {
        value = group.members.map((item) => item.value);
      } else if (group.valueType === "joinedString") {
        value = group.members.map((item) => item.value).join(",");
      } else if (group.valueType === "boolean" && group.members && group.members.length) {
        value = true;
      } else if ((group.valueType === "number" || group.valueType === "text") && group.members && group.members.length) {
        value = group.members[0].value;
      }
      return <NameValue>{
        Name: group.key,
        Value: value,
      };
    });
    return dateFiltersMap.concat(arrayFiltersMap).filter((n) => n.Value !== undefined);
  }

  public static generateExportNameValue(filter: ReviewFilter, translate: TranslateService): NameValue[] {
    const dateFilters = filter.items.filter((c) => REVIEW_DATE_TIME_KEYES.indexOf(c.key) >= 0);

    const dateFiltersMap = dateFilters.map((d: ReviewFilterItem) => <NameValue>{ Name: d.persianKey, Value: d.value });
    const arrayFilters = filter.items.filter((c) => REVIEW_DATE_TIME_KEYES.indexOf(c.key) === -1);
    const arrayFiltersMap = ReviewHelper.groupBy(arrayFilters, (c) => c.persianKey).map((group: Group<any>) => {
      if (group.valueType === "boolean" && group.members.length > 1) {
        throw Error("Boolean ValueType Should Contain Just 1 Item");
      }
      let value;
      if (group.valueType === undefined || group.valueType === "array") {
        value = group.members.map((item) => item.title).join("، ");
      } else if (group.valueType === "joinedString") {
        value = group.members.map((item) => item.title).join("، ");
      } else if (group.valueType === "boolean" && group.members && group.members.length) {
        value = translate.instant("ClientApp.Review.Yes");
      }
      return <NameValue>{
        Name: group.key,
        Value: value,
      };
    });
    return dateFiltersMap
      .concat(arrayFiltersMap)
      .filter((n) => n.Value !== undefined && n.Value !== false)
      .map((f) => ({ Name: f.Name, Value: f.Value === true ? translate.instant("ClientApp.Review.Yes") : f.Value, Operator: "" }));
  }

  public static groupBy<T>(collection: T[], key: (x: T) => string): Group<T>[] {
    // prevents the application from breaking if the array of objects doesn't exist yet
    if (!collection) {
      return null;
    }

    const groupedCollection = collection.reduce((previous, current) => {
      const groupName = key(current);
      if (!previous[groupName]) {
        previous[groupName] = [current];
      } else {
        previous[groupName].push(current);
      }
      return previous;
    }, {});

    // this will return an array of objects, each object containing a group of objects
    return Object.keys(groupedCollection).map(
      (key) =>
        <Group<T>>{
          key,
          members: groupedCollection[key],
          valueType: groupedCollection[key][0]["valueType"],
        }
    );
  }
}
